import React from "react"

import { ListItem, UnorderedList, VStack } from "@chakra-ui/react";
import Layout from "../../components/layout"
import SEO from "../../components/seo"
import {
  CTA,
  BackgroundPattern,
  Slab,
  Container,
  Title,
  Subheading,
  MainTitle,
  MainSubheading,
  Content,
  CustomerLogos,
  RatingBadge,
  CompaniesDevelopersTitle,
  CompaniesDevelopersTitleMediaAssets,
  Divider,
  DesktopOnly,
  DAMLPRatingBadge,
  StorylaneEmbed
} from "../../components/common"
import FeatureShowCase from "../../components/featuresShowcase"
import { globalStyleVariables } from "../../styles/variables"
import { DamStorageGraphic } from "../../components/graphics/dam-storage.js"
import { CDN_NODES, IMAGEKIT_PROCESSING_REGION_COUNT } from "../../data/stats"
import { dynamicAssetCreation } from "../../data/copy"
import { mediaLibrary } from "../../data/assets"

const features = [
  {
    title: "Take control of your rapidly growing media repository",
    badge: {
      label: "Manage",
      color: "imagekitYellow"
    },
    items: [
      {
        title: {
          strong: "A central place for all your digital assets",
          rest: "Get a single, cloud-based repository to streamline storage, management and distribution of product images, videos, banners, sales decks, social media & advertising graphics, and more.",
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/DAM/A%20central%20hub%20for%20all%20your%20digital%20assets.mp4?updatedAt=17140476226441&tr=w-1250",
          altText: "A central place for all your digital assets"
        }
      },
      {
        title: {
          strong: "Organize assets into folders and collections",
          rest: (
            <>
              Upload your assets in different folders, just like any other storage.<br /><br />
              Or collate them in virtual Media Collections - a unique solution that removes the hassle of reorganizing or duplicating assets or folders.
            </>
          )
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/DAM/Organize%20assets%20into%20folders%20and%20collections.mp4?updatedAt=17140476284751&tr=w-1250",
          altText: "Organize assets into folders and collections"
        }
      },
      {
        title: {
          strong: "Create a custom asset organization scheme",
          rest: "Customize asset organization using structured metadata fields and AI-powered tagging, aligning with your business's specific requirements rather than depending on just file and folder names.",
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/DAM/Create%20a%20custom%20asset%20organization%20scheme.mp4?updatedAt=17140476230481&tr=w-1250",
          altText: "Create a custom asset organization scheme"
        }
      },
      {
        title: {
          strong: "Find the right assets easily",
          rest: (
            <>
              Wondering if Jane's `ChristmasSales_Banner_5_FINAL.jpg` in your inbox is the one that goes up on your website?<br /><br />
              Leverage the AI-enhanced search that adapts to your custom asset organization, to quickly locate the right asset for your needs.
            </>
          )
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/DAM/Find%20the%20right%20assets%20easily.mp4?updatedAt=17140476232131&tr=w-1250",
          altText: "Find the right assets easily"
        }
      }
    ],
    "docLabel": "Read more about centralized DAM",
    "docLink": "/solutions/digital-asset-storage"
  },
  {
    title: "Your teams collaborate better. Your business grows faster.",
    badge: {
      label: "Collaborate",
      color: "imagekitGreen"
    },
    items: [
      {
        title: {
          strong: "Share assets with the right people",
          rest: "Say goodbye to bulky attachments and effortlessly share files, folders, and curated collections securely with stakeholders. Manage access permissions precisely for internal, external members, and even the general public.",
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/DAM/Share%20assets%20with%20the%20right%20people.mp4?updatedAt=17140476229111&tr=w-1250",
          altText: "Share assets with the right people"
        }
      },
      {
        title: {
          strong: "Speed up the feedback process",
          rest: "Consolidate all iterations, feedbacks, and approvals in one place, replacing endless back-and-forth on email or chat. Improve the time to go live with your campaigns.",
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/DAM/Speed%20up%20the%20feedback%20process.mp4?updatedAt=17140476238317&tr=w-1250",
          altText: "Speed up the feedback process"
        }
      },
      {
        title: {
          strong: "Reduce the editing back and forth",
          rest: "Browser-based image editor and AI-powered background removal reduce dependency on graphic teams, enabling marketers to swiftly make essential edits before publishing content.",
        },
        graphic: {
          videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/DAM/Reduce%20the%20editing%20back%20and%20forth.mp4?updatedAt=17140476283413&tr=w-1250",
          altText: "Reduce the editing back and forth"
        }
      }
    ],
    "docLabel": "Read more about cross-team collaboration",
    "docLink": "/solutions/dam-collaboration"
  },
  {
    coloredSlab: true,
    title: "Seamlessly collaborate & publish perfect images and videos across the web directly from the DAM",
    subTitle: "Marketing and Tech Teams",
    subHeading: (
      <VStack as={UnorderedList} pl="6" gap="1" alignItems="flex-start">
        <ListItem>Real-time Transformations</ListItem>
        <ListItem>Automatic Optimizations</ListItem>
        <ListItem>Quick load times globally</ListItem>
      </VStack>
    ),
    graphic: {
      videoUrl: "https://ik.imagekit.io/ikmedia/Graphics/2024%20Website%20Graphics/DAM/Marketing%20and%20tech%20teams%20can%20seamlessly%20collaborate.mp4?updatedAt=17140476251175&tr=w-1250",
      altText: "Seamlessly collaborate & publish perfect images and videos across the web directly from the DAM"
    }

  },
]

const IndexPage = () => (
  <Layout>
    <SEO
      title="Digital Assets Management Solution | ImageKit.io"
      description="ImageKit's Digital Asset Management is your one stop solution to store, organize, manage and collaborate on your media assets."
      meta={[
        {
          name: "robot",
          content: "noindex",
        },
      ]}
    />
    <Slab>
      <Container>
        <MainTitle as="h1" style={{ maxWidth: "1200px" }}>
          The new home for <br/>all your media assets
        </MainTitle>

        <MainSubheading>
        Move beyond Drive and Dropbox to a DAM that customizes content organization, simplifies asset search, and streamlines how teams collaborate every day.
        </MainSubheading>

        <Content
          style={{
            textAlign: "center",
          }}
        >
          <div className="cta">
            <CTA getStartedCTA title="Start for free"></CTA>
          </div>
          <div className="cta">
            <CTA
              secondary={true}
              height={"50px"}
              title="GET A DEMO"
              link="/contact-us/"
            ></CTA>
          </div>
        </Content>
        <BackgroundPattern></BackgroundPattern>
      </Container>
    </Slab>

    <Slab bg="medium">
      <Container>
        <CompaniesDevelopersTitleMediaAssets />
        <CustomerLogos
          count="6"
          grayEffect={true}
        ></CustomerLogos>
      </Container>
    </Slab>

    <DAMLPRatingBadge></DAMLPRatingBadge>

    <DesktopOnly>
      <Slab>
        <Container>
          <StorylaneEmbed url={"https://app.storylane.io/demo/jdytze9ajuzd"} />
        </Container>
      </Slab>
    </DesktopOnly>

    <FeatureShowCase
      features={features}
      startWithSolidBG={true}
    ></FeatureShowCase>


    {/* <Slab>
                <Container>
                    <Title>
                        Marketers and Creatives love ImageKit
                    </Title>
                    <Content>
                        <DAMTestimonials hardCodedTestimonials={hardCodedTestimonials} />
                    </Content>
                </Container>
            </Slab> */}

    <Slab style={{ paddingBottom: 0 }}>
      <Container>
        <Title>
          Seamlessly migrate to ImageKit DAM from any Cloud Storage
        </Title>
        <DamStorageGraphic />
      </Container>
    </Slab>
  </Layout>
)

export default IndexPage
